import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { TrendingUp, Target, Clock, Trophy } from "lucide-react";
import { api, money2, num } from "@/lib/spott";

const RANGES = [
  { id: 30, label: "30 días" },
  { id: 90, label: "90 días" },
  { id: 180, label: "6 meses" },
  { id: 365, label: "12 meses" },
];

const PIE_COLORS = ["#FFC629", "#0B2B4A", "#4B7EA8", "#9FC0D8", "#F0A400", "#C9D6E0"];

const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-spott-mid bg-white ${className}`} {...rest}>{children}</div>
);

const Kpi = ({ label, value, hint, icon: Icon, testid }) => (
  <Card className="p-6" >
    <div className="flex items-start justify-between">
      <div className="text-xs text-gray-500">{label}</div>
      {Icon && <Icon size={16} className="text-spott-yellow" />}
    </div>
    <div className="mt-2 text-2xl font-bold text-spott-yellow" data-testid={testid}>{value}</div>
    {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
  </Card>
);

const RankList = ({ title, rows, unit = "cotizaciones", testid }) => (
  <Card className="min-w-0 overflow-hidden p-6" data-testid={testid}>
    <h3 className="text-sm font-bold text-spott-blue">{title}</h3>
    {rows?.length ? (
      <ul className="mt-4 space-y-3 text-sm">
        {rows.map((r) => (
          <li key={r.name} className="flex min-w-0 items-center justify-between gap-3">
            <span className="min-w-0 flex-1 truncate text-gray-600">{r.name}</span>
            <span className="shrink-0 whitespace-nowrap text-right text-xs sm:text-sm">
              <span className="font-semibold text-spott-blue">{money2(r.revenue)}</span>
              <span className="ml-2 text-xs text-gray-400">{r.count} {unit}</span>
            </span>
          </li>
        ))}
      </ul>
    ) : <p className="mt-4 text-sm text-gray-400">Sin datos en el período.</p>}
  </Card>
);

export const AdminAnalytics = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => {
      if (alive) { setData(r.data); setBusy(false); }
    }).catch(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [days]);

  const s = data?.summary;

  return (
    <div className="mt-8 space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-spott-blue">Reportería avanzada</h2>
          <p className="text-sm text-gray-500">Desempeño comercial del pipeline de retail media.</p>
        </div>
        <div className="flex gap-2" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setDays(r.id)} data-testid={`analytics-range-${r.id}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                days === r.id ? "bg-spott-blue text-white" : "border border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
              }`}>{r.label}</button>
          ))}
        </div>
      </div>

      {busy && !data && (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-spott-yellow border-t-transparent" />
        </div>
      )}

      {s && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Cotizaciones en el período" value={num(s.quotes)} hint={`${s.sent} enviadas`}
              icon={TrendingUp} testid="analytics-quotes" />
            <Kpi label="Tasa de cierre" value={`${s.win_rate}%`} hint={`${s.approved} aprobadas`}
              icon={Trophy} testid="analytics-winrate" />
            <Kpi label="Pipeline abierto" value={money2(s.pipeline_value)} hint="Enviadas + negociación"
              icon={Target} testid="analytics-pipeline" />
            <Kpi label="Revenue ganado" value={money2(s.won_value)} hint="Cotizaciones aprobadas"
              testid="analytics-won" />
            <Kpi label="Ticket promedio" value={money2(s.avg_ticket)} testid="analytics-ticket" />
            <Kpi label="Ciclo de cierre" value={`${s.avg_cycle_days} días`} icon={Clock}
              testid="analytics-cycle" />
            <Kpi label="Leads generados" value={num(s.leads)} hint={`${s.lead_rate}% de las cotizaciones`}
              testid="analytics-leads" />
            <Kpi label="Alcance cotizado" value={num(s.total_reach)}
              hint={`Duración promedio ${s.avg_duration} meses`} testid="analytics-reach" />
          </div>

          <Card className="p-6" data-testid="analytics-trend">
            <h3 className="text-sm font-bold text-spott-blue">Tendencia de cotizaciones y valor</h3>
            <div className="mt-6 h-56 w-full sm:h-72">
              <ResponsiveContainer>
                <AreaChart data={data.trend}>
                  <defs>
                    <linearGradient id="gVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFC629" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#FFC629" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gWon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0B2B4A" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#0B2B4A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6ECF2" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#7A8A99" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#7A8A99" }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v, k) => [k === "quotes" ? num(v) : money2(v), k === "won" ? "Aprobado" : k === "value" ? "Valor cotizado" : "Cotizaciones"]} />
                  <Area type="monotone" dataKey="value" stroke="#FFC629" fill="url(#gVal)" strokeWidth={2} />
                  <Area type="monotone" dataKey="won" stroke="#0B2B4A" fill="url(#gWon)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card className="p-6" data-testid="analytics-funnel">
              <h3 className="text-sm font-bold text-spott-blue">Embudo de conversión</h3>
              <div className="mt-6 h-64 w-full">
                <ResponsiveContainer>
                  <BarChart data={data.funnel} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6ECF2" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#7A8A99" }} />
                    <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 11, fill: "#7A8A99" }} />
                    <Tooltip formatter={(v) => num(v)} />
                    <Bar dataKey="count" fill="#0B2B4A" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-4 space-y-1.5 text-xs text-gray-500">
                {data.lost.map((l) => (
                  <li key={l.stage} className="flex justify-between">
                    <span>{l.stage}</span><span>{l.count} · {money2(l.value)}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6" data-testid="analytics-industries">
              <h3 className="text-sm font-bold text-spott-blue">Revenue por industria</h3>
              <div className="mt-6 h-64 w-full">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.top_industries} dataKey="revenue" nameKey="name" innerRadius={55} outerRadius={95}
                      paddingAngle={3}>
                      {data.top_industries.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => money2(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-2 space-y-1.5 text-xs text-gray-500">
                {data.top_industries.map((r, i) => (
                  <li key={r.name} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="flex-1 truncate">{r.name}</span>
                    <span className="font-semibold text-spott-blue">{money2(r.revenue)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <RankList title="Espacios más cotizados" rows={data.top_spaces} testid="analytics-top-spaces" />
            <RankList title="Retailers con mayor demanda" rows={data.top_retailers} testid="analytics-top-retailers" />
            <RankList title="Ciudades con mayor inversión" rows={data.top_cities} testid="analytics-top-cities" />
            <RankList title="Servicios adicionales más vendidos" rows={data.top_services} unit="veces"
              testid="analytics-top-services" />
          </div>

          <Card className="p-6" data-testid="analytics-agents">
            <h3 className="text-sm font-bold text-spott-blue">Desempeño por ejecutivo</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="pb-2">Ejecutivo</th><th className="pb-2">Cotizaciones</th>
                    <th className="pb-2">Ganadas</th><th className="pb-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((a) => (
                    <tr key={a.name} className="border-t border-spott-mid/60">
                      <td className="py-2.5 text-gray-600">{a.name}</td>
                      <td className="py-2.5 text-spott-blue">{a.quotes}</td>
                      <td className="py-2.5 text-spott-blue">{a.won}</td>
                      <td className="py-2.5 font-semibold text-spott-blue">{money2(a.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
